import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Paper,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SensorsIcon from "@mui/icons-material/Sensors";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import MemoryIcon from "@mui/icons-material/Memory";
import RefreshIcon from "@mui/icons-material/Refresh";
import BarChartIcon from "@mui/icons-material/BarChart";
import DataUsageIcon from "@mui/icons-material/DataUsage";

import InfoCard from "../../../components/info-card";
import CardGrafica from "../../../components/card-grafica";
import EstadisticasTrama from "../../../components/estadisticas-trama";
import { useSensorTrama } from "../../../hooks/useSensorTrama";

const InfoSensor = ({ sensor: sensorProp, isLoading: isLoadingProp = false }) => {
  const [sensorId, setSensorId] = useState(null);
  const [sensorBasico, setSensorBasico] = useState(null);

  // Extraer ID del sensor de diferentes estructuras posibles
  useEffect(() => {
    let id = null;
    let sensorData = null;
    
    if (sensorProp && sensorProp.data && sensorProp.data._id) {
      id = sensorProp.data._id;
      sensorData = sensorProp.data;
    } else if (sensorProp && sensorProp._id) {
      id = sensorProp._id;
      sensorData = sensorProp;
    }
    
    setSensorId(id);
    setSensorBasico(sensorData);
  }, [sensorProp]);

  const {
    sensorTrama,
    isLoading,
    error,
    tieneGrafica,
    datosGrafica,
    estadisticas,
    refrescar
  } = useSensorTrama(sensorId);

  // Estado de carga combinado
  const loading = isLoadingProp || isLoading;

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Cargando datos del sensor...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton onClick={refrescar} size="small">
            <RefreshIcon />
          </IconButton>
        }
      >
        Error al cargar los datos del sensor: {error}
      </Alert>
    );
  }

  if (!sensorBasico) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No se pudo cargar la información del sensor
        </Alert>
      </Box>
    );
  }

  // Usar datos de trama si están disponibles, sino usar datos básicos
  const sensorData = sensorTrama || sensorBasico;

  return (
    <Box sx={{ p: 3 }}>
      {/* Información básica del sensor */}
      <InfoCard
        title="Información del Sensor"
        subtitle={`Tipo: ${sensorBasico?.tipo?.nombre || 'N/A'}`}
        icon={<SensorsIcon fontSize="large" />}
        items={[
          { label: 'ID', value: sensorBasico._id },
          { label: 'Nombre', value: sensorData.nombre, highlight: true },
          { label: 'Descripción', value: sensorData.descripcion || sensorBasico.alias || 'N/A' },
          { label: 'Unidades', value: sensorData.unidades || sensorBasico?.tipo?.unidades || 'N/A' },
          { label: 'Tarjeta', value: sensorBasico?.tarjeta?.nombre || 'N/A' },
          { label: 'Estado', value: sensorBasico.estatusDispositivo ? 'Activo' : 'Inactivo' }
        ]}
        chips={[
          sensorBasico.consumo 
            ? { label: 'Consumo', color: 'warning' }
            : { label: 'Medición', color: 'info' },
          tieneGrafica 
            ? { label: 'Con datos de trama', color: 'success' }
            : { label: 'Sin datos de trama', color: 'default' },
          sensorBasico.estatusDispositivo
            ? { label: 'Activo', color: 'success' }
            : { label: 'Inactivo', color: 'error' }
        ]}
        variant="elevation"
      />

      {/* Resumen de datos de trama */}
      {tieneGrafica && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Estado de Datos de Trama
            </Typography>
            <Tooltip title="Actualizar datos">
              <IconButton onClick={refrescar} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip 
              icon={<DataUsageIcon />} 
              label={`${estadisticas?.totalDatos || 0} puntos`} 
              color="primary" 
              variant="outlined" 
            />
            {estadisticas?.ultimoValor !== undefined && (
              <Chip 
                label={`Último: ${estadisticas.ultimoValor} ${sensorData.unidades || ''}`}
                color="info" 
                variant="outlined" 
              />
            )}
            <Chip 
              label={`Promedio: ${estadisticas?.promedio?.toFixed(2) || 'N/A'} ${sensorData.unidades || ''}`}
              color="secondary" 
              variant="outlined" 
            />
          </Stack>
        </Paper>
      )}

      {/* Acordeón de rangos y configuración */}
      <MuiAccordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          sx={{ 
            backgroundColor: '#1F2A38', 
            color: 'white',
            '&:hover': {
              backgroundColor: '#303C54',
            },
            '& .MuiTypography-root': {
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoryIcon />
            <Typography variant="h6">
              Configuración y Rangos
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <InfoCard
            title="Rangos de Medición"
            subtitle="Configuración de límites y resolución"
            icon={<MemoryIcon />}
            items={[
              { label: 'Rango Mínimo', value: sensorBasico?.rangoMin?.$numberDecimal || sensorBasico?.rangoMin || '0' },
              { label: 'Rango Máximo', value: sensorBasico?.rangoMax?.$numberDecimal || sensorBasico?.rangoMax || '0' },
              { label: 'Resolución Mínima', value: sensorBasico?.resolucionMin?.$numberDecimal || sensorBasico?.resolucionMin || '0' },
              { label: 'Resolución Máxima', value: sensorBasico?.resolucionMax?.$numberDecimal || sensorBasico?.resolucionMax || '0' },
              { label: 'Peligrosidad', value: sensorBasico?.peligrosidad?.$numberDecimal || sensorBasico?.peligrosidad || '0' },
              { label: 'Ajuste Peligrosidad', value: sensorBasico?.peligrosidadAjuste?.$numberDecimal || sensorBasico?.peligrosidadAjuste || '0' },
              { label: 'Posición en Trama', value: sensorBasico?.posicionEnTrama || 'N/A' }
            ]}
          />
        </AccordionDetails>
      </MuiAccordion>

      {/* Acordeón de estadísticas de trama */}
      {tieneGrafica && estadisticas && (
        <MuiAccordion sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{ 
              backgroundColor: '#1F2A38', 
              color: 'white',
              '&:hover': {
                backgroundColor: '#303C54',
              },
              '& .MuiTypography-root': {
                color: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChartIcon />
              <Typography variant="h6">
                Estadísticas de Trama
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <EstadisticasTrama 
              estadisticas={estadisticas} 
              sensor={sensorData} 
            />
          </AccordionDetails>
        </MuiAccordion>
      )}

      {/* Acordeón de gráfica de datos de trama */}
      <MuiAccordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          sx={{ 
            backgroundColor: '#1F2A38', 
            color: 'white',
            '&:hover': {
              backgroundColor: '#303C54',
            },
            '& .MuiTypography-root': {
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChartIcon />
            <Typography variant="h6">
              Gráfica de Datos de Trama
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          {tieneGrafica ? (
            <CardGrafica
              key={sensorData.nombre}
              title={`${sensorData.nombre} - Datos de Trama`}
              data={datosGrafica}
              sensor
            />
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Sin datos de trama disponibles
                </Typography>
                <Typography variant="body2">
                  Este sensor no tiene datos de trama históricos disponibles para mostrar en la gráfica.
                </Typography>
              </Alert>
              
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                <Chip 
                  label="Sin datos de trama" 
                  color="default" 
                  variant="outlined" 
                />
                <Chip 
                  label={`Tipo: ${sensorBasico?.tipo?.nombre || 'N/A'}`}
                  color="info" 
                  variant="outlined" 
                />
                <Chip
                  label={`Estado: ${sensorBasico.estatusDispositivo ? 'Activo' : 'Inactivo'}`}
                  color={sensorBasico.estatusDispositivo ? 'success' : 'error'}
                  variant="outlined"
                />
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Los datos de trama se actualizan automáticamente cada 30 segundos cuando están disponibles
                </Typography>
              </Box>
            </Paper>
          )}
        </AccordionDetails>
      </MuiAccordion>

      {/* Información de actualización */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {tieneGrafica 
            ? 'Los datos de trama se actualizan automáticamente cada 30 segundos'
            : 'Esperando datos de trama del sensor...'
          }
        </Typography>
      </Box>
    </Box>
  );
};

export default InfoSensor;